import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Bed, Users, AlertCircle, Wrench } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

export default function HouseRoomsPage() {
  const { houseId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [house, setHouse] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [formData, setFormData] = useState({
    room_no: '',
    name: '',
    capacity: '',
    gender_restriction: 'NONE',
    is_accessible: false
  });

  useEffect(() => {
    fetchHouse();
    fetchRooms();
  }, [houseId]);

  const fetchHouse = async () => {
    try {
      const response = await api.get(`/houses/${houseId}`);
      setHouse(response.data.data);
    } catch (error) {
      toast.error('Failed to load house');
      navigate('/accommodation/houses');
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await api.get(`/houses/${houseId}/rooms`);
      setRooms(response.data.data || []);
    } catch (error) {
      toast.error('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/houses/${houseId}/rooms`, {
        ...formData,
        capacity: parseInt(formData.capacity) || 1
      });
      toast.success('Room created successfully');
      setShowCreateDialog(false);
      setFormData({ room_no: '', name: '', capacity: '', gender_restriction: 'NONE', is_accessible: false });
      fetchRooms();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create room');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/houses/${houseId}/rooms/${selectedRoom.id}`, {
        ...formData,
        capacity: parseInt(formData.capacity) || 1
      });
      toast.success('Room updated successfully');
      setShowEditDialog(false);
      setSelectedRoom(null);
      fetchRooms();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update room');
    }
  };

  const handleMaintenanceToggle = async (roomId, currentStatus) => {
    try {
      await api.put(`/houses/${houseId}/rooms/${roomId}`, {
        status: currentStatus === 'MAINTENANCE' ? 'AVAILABLE' : 'MAINTENANCE'
      });
      toast.success(`Room ${currentStatus === 'MAINTENANCE' ? 'available' : 'set to maintenance'}`);
      fetchRooms();
    } catch (error) {
      toast.error('Failed to update room status');
    }
  };

  const openEditDialog = (room) => {
    setSelectedRoom(room);
    setFormData({
      room_no: room.room_no || '',
      name: room.name || '',
      capacity: room.capacity?.toString() || '',
      gender_restriction: room.gender_restriction || 'NONE',
      is_accessible: room.is_accessible || false
    });
    setShowEditDialog(true);
  };

  const getStatusBadge = (status) => {
    const variants = {
      AVAILABLE: 'default',
      OCCUPIED: 'secondary',
      MAINTENANCE: 'destructive'
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6" data-testid="house-rooms-page">
      {/* Header */}
      <div>
        <Button variant="ghost" onClick={() => navigate('/accommodation/houses')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Houses
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{house?.name} - Rooms</h1>
            <p className="text-muted-foreground mt-1">{house?.address}</p>
          </div>
          {isAdmin && (
            <Button onClick={() => setShowCreateDialog(true)} data-testid="add-room-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Room
            </Button>
          )}
        </div>
      </div>

      {/* Rooms Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {rooms.map((room) => (
          <Card key={room.id} className="hover:shadow-lg transition-shadow" data-testid={`room-card-${room.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-lg">{room.name}</CardTitle>
                  <CardDescription className="text-xs">Room {room.room_no}</CardDescription>
                </div>
                {getStatusBadge(room.status)}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="h-4 w-4" />
                  <span>Capacity: {room.capacity} {room.capacity === 1 ? 'person' : 'people'}</span>
                </div>
                {room.current_occupancy !== undefined && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Bed className="h-4 w-4" />
                    <span>Occupied: {room.current_occupancy}/{room.capacity}</span>
                  </div>
                )}
                {room.gender_restriction && room.gender_restriction !== 'NONE' && (
                  <Badge variant="outline" className="text-xs">{room.gender_restriction} only</Badge>
                )}
                {room.is_accessible && (
                  <Badge variant="outline" className="text-xs">♿ Accessible</Badge>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                {isAdmin ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => openEditDialog(room)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant={room.status === 'MAINTENANCE' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleMaintenanceToggle(room.id, room.status)}
                      data-testid={`maintenance-toggle-${room.id}`}
                    >
                      <Wrench className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <div className="text-xs text-muted-foreground py-2">View only</div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {rooms.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-2">
              <Bed className="h-12 w-12 mx-auto text-muted-foreground" />
              <h3 className="font-medium">No rooms yet</h3>
              <p className="text-sm text-muted-foreground">Add rooms to this house to get started</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-room-dialog">
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>Add New Room</DialogTitle>
              <DialogDescription>Create a new room in {house?.name}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="room_no">Room Number *</Label>
                <Input
                  id="room_no"
                  value={formData.room_no}
                  onChange={(e) => setFormData({ ...formData, room_no: e.target.value })}
                  placeholder="e.g., 101"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Room Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Blue Room"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="capacity">Capacity *</Label>
                <Input
                  id="capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  placeholder="Number of beds"
                  required
                  min="1"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gender_restriction">Gender Restriction</Label>
                <Select
                  value={formData.gender_restriction}
                  onValueChange={(value) => setFormData({ ...formData, gender_restriction: value })}
                >
                  <SelectTrigger id="gender_restriction">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">None</SelectItem>
                    <SelectItem value="MALE">Male Only</SelectItem>
                    <SelectItem value="FEMALE">Female Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_accessible"
                  checked={formData.is_accessible}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_accessible: checked })}
                />
                <Label htmlFor="is_accessible" className="cursor-pointer">Wheelchair accessible</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button type="submit">Create Room</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <form onSubmit={handleEdit}>
            <DialogHeader>
              <DialogTitle>Edit Room</DialogTitle>
              <DialogDescription>Update room information</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit_room_no">Room Number *</Label>
                <Input
                  id="edit_room_no"
                  value={formData.room_no}
                  onChange={(e) => setFormData({ ...formData, room_no: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_name">Room Name *</Label>
                <Input
                  id="edit_name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_capacity">Capacity *</Label>
                <Input
                  id="edit_capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  required
                  min="1"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_gender_restriction">Gender Restriction</Label>
                <Select
                  value={formData.gender_restriction}
                  onValueChange={(value) => setFormData({ ...formData, gender_restriction: value })}
                >
                  <SelectTrigger id="edit_gender_restriction">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">None</SelectItem>
                    <SelectItem value="MALE">Male Only</SelectItem>
                    <SelectItem value="FEMALE">Female Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit_is_accessible"
                  checked={formData.is_accessible}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_accessible: checked })}
                />
                <Label htmlFor="edit_is_accessible" className="cursor-pointer">Wheelchair accessible</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button type="submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
